// A person's accent color (slice 1c of docs/2026-10-03-ui-build-handoff.md).
//
// The choice belongs to the person, not the household, so it is stored in
// their Supabase Auth user_metadata (`accent`): no table, it follows them to
// any device, and it survives leaving or changing household. The user can
// write their own user_metadata, which is fine for a cosmetic setting; never
// trust it for anything that matters.
//
// It is also mirrored to localStorage so index.html can apply it before the
// app (and the auth session) loads, avoiding a flash of the default accent.
// The CSS side is tokensCss.ts: <html data-accent="moss"> swaps the accent.
import type { User } from "@supabase/supabase-js";
import { ACCENT_NAMES, DEFAULT_ACCENT, type AccentName } from "./constants";
import { supabase } from "./supabaseClient";

// Read by the inline script in index.html; keep the two in step.
export const ACCENT_STORAGE_KEY = "wk.accent";

export function isAccentName(value: unknown): value is AccentName {
  return typeof value === "string" && (ACCENT_NAMES as readonly string[]).includes(value);
}

export function accentFromUser(user: User | null | undefined): AccentName {
  const value: unknown = user?.user_metadata?.accent;
  return isAccentName(value) ? value : DEFAULT_ACCENT;
}

// Sets the accent on the page and remembers it on this device.
export function applyAccent(accent: AccentName): void {
  const root = document.documentElement;
  if (accent === DEFAULT_ACCENT) delete root.dataset.accent;
  else root.dataset.accent = accent;
  try {
    if (accent === DEFAULT_ACCENT) localStorage.removeItem(ACCENT_STORAGE_KEY);
    else localStorage.setItem(ACCENT_STORAGE_KEY, accent);
  } catch {
    // Storage blocked (private mode etc.): the accent still applies for now.
  }
}

// Applies the accent right away, then saves it to the person's account.
// Rejects if the save fails; the page keeps showing the new accent either way.
export async function saveAccent(accent: AccentName): Promise<void> {
  applyAccent(accent);
  const { error } = await supabase.auth.updateUser({ data: { accent } });
  if (error) throw error;
}
